import { useState, useCallback } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";

const HASURA_PROXY = "/.netlify/functions/hasura-proxy";

/**
 * Extract the operation name from a GraphQL query string
 * e.g., "query GetUsers { ... }" -> "GetUsers"
 */
function extractOperationName(query: string): string | undefined {
  const match = query.match(/^\s*(query|mutation|subscription)\s+(\w+)/i);
  return match?.[2];
}

interface UseHasuraQueryReturn<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  execute: (query: string, variables?: Record<string, unknown>) => Promise<void>;
}

/**
 * Hook for making GraphQL queries to Hasura (via proxy)
 * Used for AI assistant spaces data
 */
export function useHasuraQuery<T = unknown>(): UseHasuraQueryReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuthContext();

  const execute = useCallback(
    async (query: string, variables?: Record<string, unknown>) => {
      setIsLoading(true);
      setError(null);

      try {
        const token = getAccessToken();
        if (!token) throw new Error("No access token");
        const operationName = extractOperationName(query);
        const response = await fetch(HASURA_PROXY, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ query, variables, operationName }),
        });

        const json = await response.json();

        if (!response.ok) {
          throw new Error(json.error || `HTTP ${response.status}`);
        }

        if (json.errors && json.errors.length > 0) {
          throw new Error(json.errors.map((e: { message: string }) => e.message).join(", "));
        }

        if (json.data) {
          setData(json.data);
        }
      } catch (err) {
        console.error("[useHasuraQuery] Error:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken]
  );

  return { data, error, isLoading, execute };
}
